"use client";

import { useCallback, useEffect, useState } from "react";

import { useSession } from "@/lib/auth-client";
import { ApiConfig } from "@/lib/config";
import { dpopApi } from "@/lib/dpop-client";
import { normalizeReferralCode } from "@/lib/pending-referral";
import { getOneLink, type OneLink } from "@/lib/share-links";

// Deposit-based referral program (fexrapi docs/referral-program.md):
//   - a referred friend is "qualified" once their HL bridge deposits reach
//     terms.qualifying_deposit_usd ($10)
//   - every terms.referrals_per_reward (5) qualified friends earns the referrer
//     terms.referrer_reward_usd ($25) in their HL account, which APM then trades
//   - a referred friend whose deposits reach terms.joining_perk_deposit_usd
//     ($100) gets terms.perk_days (30) days of Fexr Plus (terms.perk_plan)
//   - the referrer's first reward comes with first_reward_free_plus_days of
//     Fexr Plus (once ever)
// Render amounts from the API (stats / public terms), never hardcode them.

/** One row of GET /v1/referrals/me. Never display referred_user_id. */
export interface Referral {
  referral_id: number;
  referrer_user_id: string;
  referred_user_id?: string | null;
  referral_date: string;
  conversion_status:
    | "pending"
    | "registered"
    | "email_verified"
    | "completed"
    | "expired";
  deposited_usd?: string | null;
  qualified_at?: string | null;
  seat_granted_at?: string | null;
}

export interface VaultReferralsSummary {
  total_referrals: number;
  converted: number;
  top_referrers: Array<{
    user_id: string;
    username: string;
    total_referrals: number;
    /** Referrals that funded (deposited the qualifying amount). */
    completed_referrals: number;
    /** $25 rewards paid. */
    total_rewards: number;
  }>;
}

export interface ReferralCode {
  referral_code: string;
  /** Same as `link.url`; null when no One Link could be issued. */
  referral_url: string | null;
  app_url?: string | null;
  /** The one-link (fexr.io/{token}) to share, issued by the API. */
  link?: OneLink | null;
}

export interface JoiningBonus {
  deposited_usd: string;
  perk_deposit_usd: string;
  perk_days: number;
  perk_plan: "plus";
  perk_unlocked_at: string | null;
  counts_for_referrer_at: string | null;
}

export interface ReferralStats {
  total_referred: number;
  total_qualified: number;
  awaiting_deposit: number;
  qualified_needed_for_next_reward: number;
  rewards_earned: number;
  rewards_paid: number;
  rewards_on_the_way: number;
  reward_usd: string;
  referrals_per_reward: number;
  qualifying_deposit_usd: string;
  /** Days of Fexr Plus that come with the referrer's first reward. */
  first_reward_free_plus_days: number;
  referrer_plus_unlocked: boolean;
  referrer_plus: { starts_at: string | null; ends_at: string | null } | null;
  next_badge: {
    slug: string;
    name: string;
    funded_friends_needed: number;
  } | null;
  /** Null unless this user joined through a referral. */
  joining_bonus: JoiningBonus | null;
}

export interface ReferralEarnings {
  total_earned_usd: string;
  paid_usd: string;
  on_the_way_usd: string;
  this_month_usd: string;
}

export type ReferralRewardStatus = "payable" | "paid" | "void";

export interface ReferralReward {
  id: string;
  batch: string;
  reward_usd: string;
  status: ReferralRewardStatus;
  created_at: string;
  paid_at: string | null;
  payout_tx_hash: string | null;
  void_reason: string | null;
  /** starting | started | joined_active_session | waiting_for_agent |
   *  waiting_for_min_balance | "failed: …" | null */
  apm_status: string | null;
  apm_session_id: string | null;
}

export interface ReferralTerms {
  referrer_reward_usd: string;
  referrals_per_reward: number;
  qualifying_deposit_usd: string;
  joining_perk_deposit_usd: string;
  perk_days: number;
  perk_plan: "plus";
}

export interface PublicReferralStats {
  monthly: Array<{
    month: string;
    paid_usd: string;
    affiliates: number;
    qualified: number;
  }>;
  total_paid_usd: string;
  earning_affiliates: number;
  qualified_referrals: number;
  joining_plus_granted: number;
  terms: ReferralTerms;
  as_of: string;
}

/** Shown before /public/stats resolves; mirrors the backend constants. */
export const DEFAULT_REFERRAL_TERMS: ReferralTerms = {
  referrer_reward_usd: "25",
  referrals_per_reward: 5,
  qualifying_deposit_usd: "10",
  joining_perk_deposit_usd: "100",
  perk_days: 30,
  perk_plan: "plus",
};

export interface GlobalLeaderboardEntry {
  rank: number;
  /** The user's own name or `user_xxxxxx` — render as plain text. */
  display_name: string;
  qualified_referrals: number;
  /** $25 rewards paid this month. */
  earned_usd: string;
}

export interface ReferralBadge {
  badge_id: string;
  slug: string;
  name: string;
  description: string | null;
  how_to_earn: string | null;
  tier_level: number | null;
  icon: string | null;
  accent_color: string | null;
  track_rank: number | null;
  earned: boolean;
  awarded_at: string | null;
  progress: { current: number; target: number } | null;
}

export interface ReferralDepositCheck {
  referred: boolean;
  /** Present and null when the user has no wallet linked. */
  wallet?: string | null;
  deposited_usd?: string;
  qualified?: boolean;
  newly_qualified?: boolean;
  joining_plus_granted?: boolean;
  joining_plus_outcome?: string | null;
  joining_plus_unlocked?: boolean;
  rewards_issued?: unknown[];
}

/** "$25" from "25.00000000" — drops trailing zero cents. */
export function formatReferralUsd(value: string | number | null | undefined) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "$0";
  return Number.isInteger(n)
    ? `$${n.toLocaleString()}`
    : `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
}

/** Prefilled share text for invites. Factual: what the friend gets. */
// Referral rewards are frozen, so the share text promises nothing.
export function referralShareText(_terms?: ReferralTerms) {
  return "Join me on Fexr.";
}

// ─── useReferrals ─────────────────────────────────────────────────────────────

export function useReferrals() {
  const { data: session, status } = useSession();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReferrals = useCallback(async () => {
    if (status !== "authenticated" || !session?.fexrApiAccessToken) {
      setReferrals([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await dpopApi.get(
        "/v1/referrals/me?limit=50&offset=0",
        session,
      );
      if (!response.ok) {
        throw new Error(`Failed to load referrals (${response.status})`);
      }
      const payload = await response.json();
      const items: Referral[] = Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload)
          ? payload
          : [];
      setReferrals(items);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load referrals.",
      );
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  }, [status, session]);

  useEffect(() => {
    if (status === "authenticated") {
      fetchReferrals();
    } else {
      setReferrals([]);
      setError(null);
    }
  }, [status, fetchReferrals]);

  // Vault invite by email/handle. Joining a vault through a valid invite
  // attributes the inviter under the same rules as a referral code.
  const createVaultInvite = useCallback(
    async (identifier: string, vaultId?: string | null) => {
      if (status !== "authenticated" || !session?.fexrApiAccessToken) {
        throw new Error("Not authenticated");
      }
      if (!vaultId) throw new Error("Vault ID required to send an invite");

      const response = await dpopApi.post(
        `/v1/vaults/${vaultId}/invite`,
        // The API takes exactly one of recipient_email / recipient_address.
        identifier.includes("@")
          ? { recipient_email: identifier }
          : { recipient_address: identifier },
        session,
      );
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Failed to send invite (${response.status})`);
      }
      return (await response.json().catch(() => ({}))) as Record<
        string,
        unknown
      >;
    },
    [status, session],
  );

  return {
    referrals,
    loading,
    error,
    refetch: fetchReferrals,
    createVaultInvite,
  };
}

// ─── useVaultReferralsSummary ──────────────────────────────────────────────────

export function useVaultReferralsSummary(vaultId?: string | null) {
  const { data: session, status } = useSession();
  const [summary, setSummary] = useState<VaultReferralsSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!vaultId || status !== "authenticated" || !session?.fexrApiAccessToken)
      return;

    setLoading(true);
    dpopApi
      .get(`/v1/vaults/${vaultId}/referrals/summary`, session)
      .then(async (res) => {
        if (!res.ok) return;
        const data = await res.json();
        setSummary(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [vaultId, status, session]);

  return { summary, loading };
}

// ─── useReferralCode ──────────────────────────────────────────────────────────

export function useReferralCode() {
  const { data: session, status } = useSession();
  const [code, setCode] = useState<ReferralCode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (status !== "authenticated" || !session?.fexrApiAccessToken) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const attempt = async () => {
      try {
        const res = await dpopApi.get("/v1/referrals/me/code", session);

        // Rare collision — keep spinner on and schedule retry (do NOT clear loading)
        if (res.status === 503 && retryCount < 3) {
          setTimeout(() => {
            if (!cancelled) setRetryCount((n) => n + 1);
          }, 5000);
          return; // loading stays true through the retry
        }

        if (!res.ok)
          throw new Error(`Failed to load referral code (${res.status})`);
        const data: ReferralCode = await res.json();
        if (!data.link?.url) {
          data.link = await getOneLink(
            { kind: "referral", ref: data.referral_code },
            session,
          ).catch(() => null);
        }
        if (!cancelled) {
          setCode(data);
          setRetryCount(0); // reset so remounts start fresh
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load code.");
          setLoading(false);
        }
      }
    };

    attempt();
    return () => {
      cancelled = true;
    };
  }, [status, session, retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  return { code, loading, error };
}

// ─── Authenticated GET helper for the read-only referral endpoints ───────────

function useAuthedJson<T>(path: string | null) {
  const { data: session, status } = useSession();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!path || status !== "authenticated" || !session?.fexrApiAccessToken)
      return;

    setLoading(true);
    setError(null);
    try {
      const res = await dpopApi.get(path, session);
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setData((await res.json()) as T);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setLoading(false);
    }
  }, [path, status, session]);

  useEffect(() => {
    if (status === "authenticated") load();
  }, [status, load]);

  return { data, loading, error, refetch: load };
}

// ─── useReferralStats ─────────────────────────────────────────────────────────

export function useReferralStats() {
  const { data, loading, error, refetch } = useAuthedJson<ReferralStats>(
    "/v1/referrals/me/stats",
  );
  return { stats: data, loading, error, refetch };
}

// ─── useReferralEarnings ──────────────────────────────────────────────────────

export function useReferralEarnings() {
  const { data, loading, error, refetch } = useAuthedJson<ReferralEarnings>(
    "/v1/referrals/me/earnings",
  );
  return { earnings: data, loading, error, refetch };
}

// ─── useReferralRewards ───────────────────────────────────────────────────────

export function useReferralRewards(offset = 0, limit = 50) {
  const { data, loading, error, refetch } = useAuthedJson<{
    items: ReferralReward[];
    total: number;
  }>(`/v1/referrals/me/rewards?offset=${offset}&limit=${limit}`);
  return {
    rewards: data?.items ?? [],
    total: data?.total ?? 0,
    loading,
    error,
    refetch,
  };
}

// ─── useReferralBadges ────────────────────────────────────────────────────────

export function useReferralBadges() {
  const { data, loading, error, refetch } = useAuthedJson<{
    tracks: Array<{ track: string; title: string; badges: ReferralBadge[] }>;
  }>("/v1/users/me/badges/catalog");
  const badges =
    data?.tracks?.find((t) => t.track === "referral")?.badges ?? [];
  return { badges, loading, error, refetch };
}

// ─── usePublicReferralStats ───────────────────────────────────────────────────

let publicStatsCache: { at: number; data: PublicReferralStats } | null = null;
let publicStatsInFlight: Promise<PublicReferralStats | null> | null = null;

/** GET /v1/referrals/public/stats, shared across callers; cached 5 min. */
export function fetchPublicReferralStats(): Promise<PublicReferralStats | null> {
  if (publicStatsCache && Date.now() - publicStatsCache.at < 5 * 60_000)
    return Promise.resolve(publicStatsCache.data);
  if (!publicStatsInFlight) {
    publicStatsInFlight = globalThis
      .fetch(ApiConfig.getApiEndpoint("/v1/referrals/public/stats"))
      .then((res) => (res.ok ? res.json() : null))
      .then((data: PublicReferralStats | null) => {
        if (data) publicStatsCache = { at: Date.now(), data };
        return data;
      })
      .catch(() => null)
      .finally(() => {
        publicStatsInFlight = null;
      });
  }
  return publicStatsInFlight;
}

/** Program terms from the API, falling back to the backend defaults. */
export async function getReferralTerms(): Promise<ReferralTerms> {
  return (await fetchPublicReferralStats())?.terms ?? DEFAULT_REFERRAL_TERMS;
}

/** Public program totals + terms. Works signed out. */
export function usePublicReferralStats() {
  const [stats, setStats] = useState<PublicReferralStats | null>(
    publicStatsCache?.data ?? null,
  );

  useEffect(() => {
    let cancelled = false;
    fetchPublicReferralStats().then((data) => {
      if (data && !cancelled) setStats(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { stats, terms: stats?.terms ?? DEFAULT_REFERRAL_TERMS };
}

// ─── useGlobalLeaderboard ─────────────────────────────────────────────────────

export function useGlobalLeaderboard() {
  const [entries, setEntries] = useState<GlobalLeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState(0);

  const doFetch = useCallback(
    async (force = false) => {
      const now = Date.now();
      // Server caches 5 minutes; don't refetch more often unless forced.
      if (!force && now - lastFetched < 5 * 60 * 1000 && entries.length > 0)
        return;

      setLoading(true);
      setError(null);
      try {
        // Public endpoint — no auth header needed.
        const res = await globalThis.fetch(
          ApiConfig.getApiEndpoint("/v1/referrals/leaderboard"),
        );
        if (!res.ok) throw new Error(`Leaderboard unavailable (${res.status})`);
        const data: GlobalLeaderboardEntry[] = await res.json();
        setEntries(Array.isArray(data) ? data : []);
        setLastFetched(now);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load leaderboard.",
        );
      } finally {
        setLoading(false);
      }
    },
    [lastFetched, entries.length],
  );

  useEffect(() => {
    doFetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { entries, loading, error, refetch: () => doFetch(true) };
}

// ─── checkReferralDeposit ─────────────────────────────────────────────────────

/**
 * POST /v1/referrals/me/check-deposit — call once after a deposit lands so the
 * joining seat and the referrer's count update immediately. Retries 503 (HL
 * unreadable) with backoff; resolves null on failure. Never blocks the caller's
 * deposit UI — fire and forget.
 */
export async function checkReferralDeposit(
  session: Parameters<typeof dpopApi.post>[2],
  attempts = 3,
): Promise<ReferralDepositCheck | null> {
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await dpopApi.post(
        "/v1/referrals/me/check-deposit",
        {},
        session,
      );
      if (res.ok) return (await res.json()) as ReferralDepositCheck;
      if (res.status !== 503) return null;
    } catch {}
    await new Promise((r) => setTimeout(r, 2000 * 2 ** i));
  }
  return null;
}

// ─── useApplyReferralCode ─────────────────────────────────────────────────────

const WALLET_ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;
const REFERRAL_CODE_RE = /^[A-Z0-9]{6,12}$/;

/** Manual entry accepts a referral code or the referrer's wallet address. */
export function isReferralInput(value: string): boolean {
  const v = value.trim();
  return (
    WALLET_ADDRESS_RE.test(v) || REFERRAL_CODE_RE.test(normalizeReferralCode(v))
  );
}

export function useApplyReferralCode() {
  const { data: session, status } = useSession();
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = useCallback(
    async (input: string): Promise<boolean> => {
      if (status !== "authenticated" || !session?.fexrApiAccessToken) {
        setError("Sign in to apply a referral code");
        return false;
      }

      const raw = input.trim();
      const isAddress = WALLET_ADDRESS_RE.test(raw);
      const code = normalizeReferralCode(raw);
      if (!isAddress && !REFERRAL_CODE_RE.test(code)) {
        setError("Code not valid");
        return false;
      }

      setApplying(true);
      setError(null);
      try {
        const res = isAddress
          ? await dpopApi.post(
              "/v1/referrals/from-address",
              { referrer_address: raw },
              session,
            )
          : await dpopApi.post(
              "/v1/referrals/me/apply-code",
              { referral_code: code },
              session,
            );

        if (res.status === 429) {
          setError("Too many attempts. Try again later.");
          return false;
        }
        if (res.status === 422 || res.status === 404 || res.status === 400) {
          setError(
            isAddress
              ? "No Fexr account found for that address, or it can't be linked"
              : "Code not valid",
          );
          return false;
        }
        if (!res.ok) {
          setError("Something went wrong. Try again.");
          return false;
        }

        setApplied(true);
        return true;
      } catch {
        setError("Something went wrong. Try again.");
        return false;
      } finally {
        setApplying(false);
      }
    },
    [status, session],
  );

  return { apply, applying, applied, error, clearError: () => setError(null) };
}
